// A card turning over (owner's pick 2026-10-16, variant A of four tried on the dev server: today's flat
// CSS turn slowed down, a production-like narrowing, slide-then-turn, and this one): a real 3D turn with
// depth (the card's perspective is set in board.css), the card lifting and growing a little half way and
// landing again. 400 ms, the same time the card takes to glide from the stock to the waste, so the turn
// is seen travelling from one pile to the other. Run from CardEl when the face changes; the CSS has no
// transition on .card__inner, so this is the only turn. It runs whatever the system's "reduce motion"
// setting says (owner 2026-10-23: older players never chose it and missed the animations).
import { effectsOff } from '../ui/effects';

export const FLIP_MS = 400;
/** The legacy card turn: 110 ms narrowing + 110 ms widening (card.js setReversed, Fx.Morph linear). */
const LEGACY_TURN_MS = 220;

/** `duration`: the deal turns a card within its flight to the column (CardEl, DEAL_FLIGHT_MS). */
export function animateFlip(inner: HTMLElement, toUp: boolean, delay: number, duration = FLIP_MS): Animation | undefined {
  const from = toUp ? 180 : 0;
  const to = toUp ? 0 : 180;
  // 3D effects off (ui/effects.ts): the legacy turn (card.js setReversed): the card narrows to a line about its
  // middle and widens again with the other face, 110 + 110 ms, linear, in the middle of its flight. A rotateY
  // with no perspective (app.css) draws exactly that, flat, and costs no more than a slide (owner 2026-10-23).
  if (effectsOff()) {
    const legacy = Math.min(LEGACY_TURN_MS, duration);
    return inner.animate([{ transform: `rotateY(${from}deg)` }, { transform: `rotateY(${to}deg)` }], { duration: legacy, delay: delay + (duration - legacy) / 2, easing: 'linear', fill: 'backwards' });
  }
  return inner.animate(
    [
      { transform: `translateY(0) rotateY(${from}deg) scale(1)` },
      { offset: 0.5, transform: `translateY(-14%) rotateY(${(from + to) / 2}deg) scale(1.14)` },
      { transform: `translateY(0) rotateY(${to}deg) scale(1)` },
    ],
    { duration, delay, easing: 'ease-in-out', fill: 'backwards' },
  );
}

/** The classic turn (engine/motion.ts, legacy card.js setReversed): the card narrows to 5 px of 109 in 110 ms and
 *  widens again with the other face in 110 ms, its width changing linearly. `anchor`: `centre` narrows about the
 *  middle (a column card, the turn-three waste, a Spider stock deal); `left` keeps the left edge (legacy
 *  `onlyWidthEffect`: a card turned from the stock or back onto it, turning while it slides). `delay`: legacy
 *  turns some cards only after they land (turn three, the Spider stock deal). A flat scaleX with the faces
 *  swapped at the narrowest (the rotateY jumps there), so it costs no more than a slide. */
export function animateClassicFlip(inner: HTMLElement, toUp: boolean, anchor: 'centre' | 'left', delay = 0): Animation {
  const from = toUp ? 180 : 0;
  const to = toUp ? 0 : 180;
  const MIN = 5 / 109;
  const at = (s: number, deg: number) => `translateX(${anchor === 'left' ? (-(1 - s) * 50).toFixed(2) : 0}%) rotateY(${deg}deg) scaleX(${s.toFixed(3)})`;
  const half = [0, 0.25, 0.5, 0.75, 1];
  const frames: Keyframe[] = [
    ...half.map((q) => ({ offset: q / 2, transform: at(1 - q * (1 - MIN), from) })),
    ...half.map((q) => ({ offset: 0.5 + q / 2, transform: at(MIN + q * (1 - MIN), to) })),
  ];
  return inner.animate(frames, { duration: LEGACY_TURN_MS, delay, easing: 'linear', fill: 'backwards' });
}

/** A card dealt face down rises a little in 3D on its way to the column and lands (owner 2026-10-16: every
 *  card of the deal moves in 3D); `down` keeps its back turned up. */
export function animateLift(inner: HTMLElement, down: boolean, duration: number): Animation | undefined {
  if (effectsOff()) return undefined; // 3D effects off: the card glides flat, like the legacy game
  const turn = down ? 'rotateY(180deg)' : 'rotateY(0deg)';
  return inner.animate(
    [
      { transform: `translateY(0) ${turn} rotateX(0deg) scale(1)` },
      { offset: 0.5, transform: `translateY(-10%) ${turn} rotateX(12deg) scale(1.08)` },
      { transform: `translateY(0) ${turn} rotateX(0deg) scale(1)` },
    ],
    { duration, easing: 'ease-in-out' },
  );
}
